import { config } from '@psl/functions/config';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { showInfo } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow.utils';
import {
  CreateLinkRequestMethodEnum,
  CreateLinkRequestTypeEnum,
  CreateLinkResponse,
  LinkActions,
  LinksApi,
  MessageApi,
} from '../../generated/api';
import { assertNever } from '../../utils/typeHelper';
import { authState$ } from '../auth/utils';
import { userProfile$ } from '../firebase/observables/userProfile$';
import { SpecialUrlCode } from '../urlCodeHelpers';
import { ShareTarget } from './ShareTarget';
import { shareTargetToDynamicLinkRequest } from './_/ServerActuallyExpectedShareTarget';

const defaultShareWay = CreateLinkRequestMethodEnum.Generic;

const defaultAdditionActions = (_: ShareTarget): LinkActions => {
  return {};
};

export interface CreateShareDynamicLinkOptions {
  social?: ShareLinkSocialInfos;
  shareWay?: CreateLinkRequestMethodEnum;
  extraQuery?: Partial<Record<string, string>>;
  additionActions?: LinkActions;
}

export interface CreateShareRawLinkOptions {
  extraQuery?: Partial<Record<string, string>>;
}

export interface CreateShareLinkOptions {
  social?: ShareLinkSocialInfos;
  shareWay?: CreateLinkRequestMethodEnum;
  extraQuery?: Partial<Record<string, string>>;
  additionActions?: LinkActions;
  preferOriginalLink?: boolean;
}

export interface ShareLinkSocialInfos {
  title: string;
  description: string;
  image: string;
}

const isLoggedIn$ = authState$.pipe(map(user => Boolean(user)));

export class LinkServiceHelper {
  constructor(
    private messageService: MessageApi,
    private linkService: LinksApi,
  ) {}

  async createShareLink(
    shareTarget: ShareTarget,
    options: CreateShareLinkOptions = {},
  ): Promise<string> {
    if (
      (await firstValueFrom(isLoggedIn$)) &&
      (shareTarget.type !== 'listing' || shareTarget.inShow.type === 'liveShow')
    ) {
      const res = await this.createShareDynamicLink(shareTarget, options);
      return options.preferOriginalLink ? res.originalLink : res.shortLink;
    } else {
      return await this.createShareRawLink(shareTarget, {
        extraQuery: options.extraQuery,
      });
    }
  }

  async createShareDynamicLink(
    shareTarget: ShareTarget,
    options: CreateShareDynamicLinkOptions = {},
  ): Promise<CreateLinkResponse> {
    const shareWay = options.shareWay ?? defaultShareWay;

    if (shareTarget.type === 'review') {
      const resp =
        await this.messageService.messageApiControllerCreateMessageLink({
          messageId: shareTarget.reviewId,
          createMessageLinkRequest: {},
        });
      return { id: '', ...resp, originalLink: resp.shortLink };
    }

    const additionActions =
      options.additionActions ?? defaultAdditionActions(shareTarget);

    const parsedRequest = shareTargetToDynamicLinkRequest(shareTarget);

    return this.linkService.linksApiControllerCreateLink({
      createLinkRequest: {
        type: CreateLinkRequestTypeEnum.Share,
        method: shareWay,
        actions: additionActions,
        linkParams: options.extraQuery,
        shareTarget: parsedRequest.serverShareTarget,
        social: options.social && {
          st: options.social.title,
          sd: options.social.description,
          si: options.social.image,
        },
      },
    });
  }

  async createShareRawLink(
    shareTarget: ShareTarget,
    options: CreateShareRawLinkOptions = {},
  ): Promise<string> {
    let urlPath: string;
    let urlQuery: Record<string, string> = {};

    if (shareTarget.type === 'listing') {
      if (shareTarget.inShow.type === 'liveShow') {
        urlPath = SpecialUrlCode.profile(
          showInfo(shareTarget.inShow).seller.id,
        );
        urlQuery = {
          ...urlQuery,
          present: `listing_${shareTarget.listingId}`,
        };
      } else {
        urlPath = `products/${shareTarget.sellerId}/${shareTarget.listingId}`;
      }
    } else if (shareTarget.type === 'giveaway') {
      urlPath = SpecialUrlCode.profile(showInfo(shareTarget.inShow).seller.id);
      urlQuery = {
        ...urlQuery,
        present: `giveaway_${shareTarget.giveawayId}`,
      };
    } else if (shareTarget.type === 'profile') {
      const profile = await firstValueFrom(userProfile$(shareTarget.profileId));
      if (profile?.urlCode) {
        urlPath = profile.urlCode;
      } else {
        // use url in form of /u_<uid> for backward compatibility
        urlPath = SpecialUrlCode.profile(shareTarget.profileId);
      }
    } else if (shareTarget.type === 'store') {
      urlPath = SpecialUrlCode.profile(shareTarget.storeId);
    } else if (shareTarget.type === 'upcomingShow') {
      urlPath = SpecialUrlCode.upcomingShow(shareTarget.upcomingShowId);
    } else if (shareTarget.type === 'liveShow') {
      urlPath = SpecialUrlCode.liveShow(shareTarget.liveShowId);
    } else if (shareTarget.type === 'replay') {
      urlPath = SpecialUrlCode.replay(shareTarget.liveShowId);
    } else if (shareTarget.type === 'event') {
      urlPath = shareTarget.eventUrlCode;
    } else if (shareTarget.type === 'eventTicket') {
      urlPath = SpecialUrlCode.event(shareTarget.eventId);
      urlQuery = {
        ...urlQuery,
        present: `listing_${shareTarget.ticketId}`,
      };
    } else if (shareTarget.type === 'promotion') {
      urlPath = SpecialUrlCode.promotion(shareTarget.promotionId);
    } else if (shareTarget.type === 'review') {
      urlPath = SpecialUrlCode.review(shareTarget.reviewId);
    } else if (shareTarget.type === 'popclip') {
      urlPath = SpecialUrlCode.popclipV1(
        shareTarget.sellerId,
        shareTarget.clipId,
      );
    } else {
      assertNever(shareTarget);
    }

    const querystring = new URLSearchParams({
      ...(options.extraQuery as Record<string, string>),
      ...urlQuery,
    }).toString();

    return querystring
      ? `${config.url}/${urlPath}?${querystring}`
      : `${config.url}/${urlPath}`;
  }
}
